import React, { useState } from 'react';
import { Body1, Body5, Container } from '@ui';
import styled from 'styled-components';
import { useFileUploader, useUploadFormFiles } from '@hooks/features/useUploadFormFiles';
import { FlatButton } from '@ui/buttons';
import { uploadFilesActions } from '@constants/actionTypes';
import { useDispatch } from 'react-redux';
import { CloseButton } from '@ui/buttons/CloseButton';
import { DocumentIcon } from '@assets/icons/svgs/DocumentIcon';
import { Edit } from '@assets/icons/svgs/Edit';
import { UPLOAD_DOC_TYPE_OPTIONS } from '@constants';
import { SearchAutocompleteWrapper } from './components/SearchAutocomplete';
import { CompanySelectionInput, CompanySelectionInputStyling } from './components/CompanySelectionInput';
import { Gear } from '@assets/icons/svgs';
import { Modal } from '@ui';
import { sleep } from '@utils/usefulFuncs';
import { showErrorBanner, showSuccessBanner } from '@utils/application';

const Wrapper = styled.div``;
const CustomFileRowWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 24px auto;
  gap: 16px;
  padding-bottom: 8px;

  height: 66px;
`;
const FileRowWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 24px 1fr 1fr auto; /* Creates a row for each element */
  gap: 16px;
  padding-bottom: 8px;

  height: 66px;
`;
const CloseButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  height: 42px;

  margin-left: 8px;

  svg {
    padding-top: 1px;
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const DocumentIconWrapper = styled.div`
  padding-right: 8px;

  svg {
    padding-top: 1px;
    height: 18px;
    width: 18px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const EditIconWrapper = styled.div`
  padding-left: 8px;

  svg {
    padding-top: 1px;
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const CompanySelectorWrapper = styled.div`
  ${SearchAutocompleteWrapper} {
    width: 100%;
  }

  ${CompanySelectionInputStyling} {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;

    padding: 0 8px;
    height: 34px;

    input {
      width: 100%;
      padding: 0 !important;
    }
  }

  input::placeholder {
    color: ${({ theme }) => theme.themeColors.text} !important;
    opacity: 1;
  }
`;
const ExtraActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const AdditionalFileParamsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;
const GearIconWrapper = styled.div`
  cursor: pointer;

  svg {
    height: 18px;
    width: 18px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
`;
const FileNameWrapper = styled.div`
  flex: 1;
  width: 100%;
  cursor: pointer;
`;

const DocId = ({ name, type, onEditFile }) => {
  const [editValue, setEditValue] = useState(name);

  const onFileNameChange = (e) => {
    const v = e.target.value;
    setEditValue(v);
    onEditFile('docId', v);
  };

  return (
    <Container row verticallyCenter>
      <DocumentIconWrapper>
        <Container row centerAll>
          <Body5 bold>&nbsp;ID</Body5>
        </Container>
      </DocumentIconWrapper>
      <input value={editValue} onChange={onFileNameChange} autoFocus />
    </Container>
  );
};

const CloseModalButtonWrapper = styled.div`
  cursor: pointer;

  z-index: 1116;

  * {
    z-index: 1116;
  }

  svg {
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const AdvancedSettingsWrapper = styled.div`
  padding: 0;

  min-width: 340px;
`;
const CheckboxInput = styled.input`
  cursor: pointer;
  margin-top: 0;
  margin-left: 0px;
  width: 14px !important;
`;
const CheckboxLabel = styled.label`
  cursor: pointer;
  margin: 0;
  padding-left: 8px;
  user-select: none;
`;
const CheckboxLabelNoIndent = styled.label`
  cursor: pointer;
  margin: 0;
  user-select: none;
`;

const ParamSetting = ({ setting, file, onEditFile }) => {
  const apiKeyName = setting.apiKeyName;
  const fieldLabel = setting.displayName;
  const value = file[apiKeyName];

  React.useEffect(() => {
    const apiKeyName = setting.apiKeyName;
    const value = file[apiKeyName];
    if (value === undefined) {
      const defaultValue =
        apiKeyName === 'extract_formula_dependencies' || apiKeyName === 'use_adaptive_table_extraction' ? false : '';
      onEditFile(apiKeyName, defaultValue);
    }
  }, []);

  const onCheckboxChange = () => {
    onEditFile(apiKeyName, !value);
  };

  const onValueChange = (e) => {
    onEditFile(apiKeyName, e.target.value);
  };

  if (setting.apiKeyName === 'extract_formula_dependencies') {
    return (
      <Container row verticallyCenter>
        <CheckboxInput type="checkbox" id={'checkbox-' + fieldLabel} checked={value} onClick={onCheckboxChange} />
        <CheckboxLabel htmlFor={'checkbox-' + fieldLabel} className="not-selectable">
          <Container bottom={3}>
            <Body5>{fieldLabel}</Body5>
          </Container>
        </CheckboxLabel>
      </Container>
    );
  }

  if (setting.apiKeyName === 'use_adaptive_table_extraction') {
    return (
      <Container row verticallyCenter spaceBetween>
        <CheckboxLabelNoIndent htmlFor={'checkbox-' + fieldLabel} className="not-selectable">
          <Container bottom={3}>
            <Body5>Use Adaptive Table Extraction</Body5>
          </Container>
        </CheckboxLabelNoIndent>
        <CheckboxInput type="checkbox" id={'checkbox-' + fieldLabel} checked={value} onClick={onCheckboxChange} />
      </Container>
    );
  }
  if (setting.apiKeyName === 'do_table_extraction') {
    return (
      <Container row verticallyCenter spaceBetween top={16}>
        <CheckboxLabelNoIndent htmlFor={'checkbox-' + fieldLabel} className="not-selectable">
          <Container bottom={3}>
            <Body5>Do Table Extraction</Body5>
          </Container>
        </CheckboxLabelNoIndent>
        <CheckboxInput type="checkbox" id={'checkbox-' + fieldLabel} checked={value} onClick={onCheckboxChange} />
      </Container>
    );
  }
  if (setting.apiKeyName === 'do_vision_extraction') {
    return (
      <Container row verticallyCenter spaceBetween top={16}>
        <CheckboxLabelNoIndent htmlFor={'checkbox-' + fieldLabel} className="not-selectable">
          <Container bottom={3}>
            <Body5>Do Vision Extraction</Body5>
          </Container>
        </CheckboxLabelNoIndent>
        <CheckboxInput type="checkbox" id={'checkbox-' + fieldLabel} checked={value} onClick={onCheckboxChange} />
      </Container>
    );
  }

  if (setting.apiKeyName === 'table_extraction_tolerance') {
    return (
      <Container row verticallyCenter spaceBetween top={16}>
        <Container bottom={3}>
          <Body5>Table Extraction Tolerance</Body5>
        </Container>
        <Container left={16}>
          <input type="number" value={value} onChange={onValueChange} />
        </Container>
      </Container>
    );
  }

  return null;
};
const AdditionalFileParamsModal = ({ isOpen, file, onClose, onEditFile, settings }) => {
  if (!isOpen) return null;

  return (
    <Modal onDismiss={onClose}>
      <AdvancedSettingsWrapper>
        <Container row verticallyCenter spaceBetween>
          <Container>
            <Body1 bold>File Upload Parameters</Body1>
          </Container>
          <CloseModalButtonWrapper>
            <CloseButton onClick={onClose} />
          </CloseModalButtonWrapper>
        </Container>
        <Container top={24}>
          {settings.map((setting) => (
            <ParamSetting key={setting.keyName} onEditFile={onEditFile} file={file} setting={setting} />
          ))}
        </Container>
      </AdvancedSettingsWrapper>
    </Modal>
  );
};

const extractFormula = { apiKeyName: 'extract_formula_dependencies', displayName: 'Extract Formula Dependencies' };
const tableExtractionTolerance = {
  apiKeyName: 'table_extraction_tolerance',
  displayName: 'Table Extraction Tolerance',
};
const doTableExtraction = {
  apiKeyName: 'do_table_extraction',
  displayName: 'Do Table Extraction',
};
const doVisionExtraction = {
  apiKeyName: 'do_vision_extraction',
  displayName: 'Do Vision Extraction',
};
const tableExtractionFileTypes = ['pdf', 'doc', 'docx'];

const AdditionalFileParams = ({ file, onEditFile }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [settings, setSettings] = React.useState([]);

  React.useEffect(() => {
    if (['xls', 'xlsx', 'xlsm', 'xlsb'].includes(file.type)) {
      setSettings([extractFormula]);
    }
    if (tableExtractionFileTypes.includes(file.type)) {
      setSettings([tableExtractionTolerance, doTableExtraction, doVisionExtraction]);
    }
  }, [file]);

  const handleOpenClick = () => {
    setIsOpen(true);
  };
  const handleCloseModal = () => {
    setIsOpen(false);
  };

  if (settings.length === 0) {
    return <div />;
  }

  return (
    <AdditionalFileParamsWrapper>
      <AdditionalFileParamsModal
        isOpen={isOpen}
        file={file}
        onClose={handleCloseModal}
        onEditFile={onEditFile}
        settings={settings}
      />
      <GearIconWrapper onClick={handleOpenClick}>
        <Gear />
      </GearIconWrapper>
    </AdditionalFileParamsWrapper>
  );
};

const FileName = ({ name, type, onEditFile }) => {
  const [editValue, setEditValue] = useState(name);
  const [isEditing, setIsEditing] = useState(false);

  const onEditClick = () => setIsEditing(true);

  const onFileNameChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleSave = (v) => {
    onEditFile('name', v);
    setIsEditing(false);
  };

  React.useEffect(() => {
    function handleKeyPress(event) {
      if (event.key === 'Enter') {
        handleSave(editValue);
      }
    }

    if (isEditing) {
      window.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isEditing, editValue]);

  if (isEditing) {
    return (
      <Container row verticallyCenter>
        <DocumentIconWrapper>
          <DocumentIcon />
        </DocumentIconWrapper>
        <input value={editValue} onChange={onFileNameChange} onBlur={() => handleSave(editValue)} autoFocus />
        <Container left={2}>
          <Body5>.{type}</Body5>
        </Container>
      </Container>
    );
  }
  return (
    <Container row verticallyCenter fullWidth>
      <DocumentIconWrapper>
        <DocumentIcon />
      </DocumentIconWrapper>
      <FileNameWrapper onClick={onEditClick}>
        <Container row verticallyCenter fullWidth>
          <div style={{ flex: 1 }}>
            <Body5>
              {name}.{type}
            </Body5>
          </div>

          <EditIconWrapper onClick={onEditClick}>
            <Edit />
          </EditIconWrapper>
        </Container>
      </FileNameWrapper>
    </Container>
  );
};

const CompanySelect = ({ company, onEditFile }) => {
  const allowOnlyCompanyTagsInSearch = false; // useCurrentUser().custom_client_id === 37;

  const handleCompanyTagSelect = (v) => {
    onEditFile('company', v);
  };
  const handleRemove = () => {
    onEditFile('company', null);
  };
  return (
    <CompanySelectorWrapper>
      <Container row verticallyCenter height={66} fullWidth>
        <CompanySelectionInput
          value={company}
          label={null}
          onAddCompany={handleCompanyTagSelect}
          onRemoveCompany={handleRemove}
          placeholder={'Assign Company / Tag'}
          enableCreateTag
          alwaysHideClear
          onlyCompanyTags={allowOnlyCompanyTagsInSearch}
        />
      </Container>
    </CompanySelectorWrapper>
  );
};

const DocTypeSelect = ({ docType, onEditFile }) => {
  const handleDocTypeSelect = (e) => {
    onEditFile('docType', e.target.value);
  };
  return (
    <Container row verticallyCenter height={66}>
      <select value={docType} onChange={handleDocTypeSelect}>
        <option value={''}>Document Type...</option>
        {UPLOAD_DOC_TYPE_OPTIONS.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    </Container>
  );
};

const FileRow = ({ file, onEditCompany }) => {
  const dispatch = useDispatch();

  const { name, type, company, docType } = file;

  const onDeleteFile = () => {
    dispatch({
      type: uploadFilesActions.deleteFile,
      payload: file.name,
    });
  };

  const onEditFile = (key, value) => {
    if (key === 'company' && !!value) {
      onEditCompany(value);
    }

    dispatch({
      type: uploadFilesActions.updateFiles,
      payload: [{ prevName: file.name, changes: { [key]: value } }],
    });
  };

  return (
    <FileRowWrapper>
      <FileName name={file.name} type={file.type} onEditFile={onEditFile} />
      <AdditionalFileParams file={file} onEditFile={onEditFile} />
      <CompanySelect company={company} onEditFile={onEditFile} />
      <DocTypeSelect docType={docType} onEditFile={onEditFile} />
      <Container row verticallyCenter height={66}>
        <CloseButtonWrapper>
          <CloseButton onClick={onDeleteFile} />
        </CloseButtonWrapper>
      </Container>
    </FileRowWrapper>
  );
};

export const UploadFilesList = ({ reupload, isUploading, setIsUploading }) => {
  const dispatch = useDispatch();
  const [updateAllFilesCompanyTag, setUpdateAllFilesCompanyTag] = React.useState(true);

  const files = useUploadFormFiles();
  const onUploadFile = useFileUploader(reupload);

  const updateAllPendingFilesWithCompany = (company) => {
    if (updateAllFilesCompanyTag && company !== null) {
      files.forEach((file) => {
        dispatch({
          type: uploadFilesActions.updateFiles,
          payload: [{ prevName: file.name, changes: { company: company } }],
        });
      });
    }
  };

  const handleUploadAll = async () => {
    setIsUploading(true);
    const responses = await Promise.all(files.map((file) => onUploadFile(file)));
    const successFiles = responses.map((wasSuccess, index) => (wasSuccess ? files[index] : null));
    if (successFiles.length > 0) {
      successFiles.forEach((file) => {
        if (file) {
          dispatch({
            type: uploadFilesActions.deleteFile,
            payload: file.name,
          });
        }
      });
    }
    const failedFilesCount = files.length - successFiles.filter((f) => !!f).length;
    if (failedFilesCount > 0) {
      showErrorBanner(
        `${failedFilesCount} file${failedFilesCount > 1 ? 's' : ''} failed to upload, please try again.`,
        null,
        dispatch
      );
    } else {
      showSuccessBanner(
        `${successFiles.length} file${
          successFiles.length > 1 ? 's' : ''
        } uploaded successfully, processing status will be available shortly.`,
        null,
        dispatch
      );
    }
    await sleep(300);
    setIsUploading(false);
  };

  const toggleCompanyTagSet = () => setUpdateAllFilesCompanyTag(!updateAllFilesCompanyTag);
  return (
    <Wrapper>
      <ExtraActionsWrapper>
        {files.length > 0 && (
          <Container top={8} row verticallyCenter>
            <input
              type="checkbox"
              id="updateAllFilesCompanyTag"
              checked={updateAllFilesCompanyTag}
              onClick={toggleCompanyTagSet}
              style={{ cursor: 'pointer', marginTop: 0, marginLeft: '0px', width: '14px' }}
            />
            <label
              htmlFor="updateAllFilesCompanyTag"
              className="not-selectable"
              style={{ cursor: 'pointer', margin: 0, paddingLeft: '8px' }}
            >
              <Container>
                <Body5>Assign the same Company / Tag to all files</Body5>
              </Container>
            </label>
          </Container>
        )}
      </ExtraActionsWrapper>

      {files.map((file) => (
        <FileRow
          key={file.name}
          file={file}
          onUploadFile={onUploadFile}
          reupload={reupload}
          onEditCompany={updateAllPendingFilesWithCompany}
        />
      ))}
      {files.length > 0 && (
        <Container top={24} style={{ textAlign: 'center' }}>
          <FlatButton fullWidth onClick={handleUploadAll} disabled={isUploading || files.length === 0}>
            Upload Files
          </FlatButton>
        </Container>
      )}
    </Wrapper>
  );
};

const CustomFileRow = ({ file, onEditFile }) => {
  const dispatch = useDispatch();

  const handleEditFile = (key, value) => {
    onEditFile(file, key, value);
  };

  const onDeleteFile = () => {
    dispatch({
      type: uploadFilesActions.deleteFile,
      payload: file.name,
    });
  };

  return (
    <CustomFileRowWrapper>
      <FileName name={file.name} type={file.type} onEditFile={handleEditFile} />
      <Container row verticallyCenter height={66}>
        <CloseButtonWrapper>
          <CloseButton onClick={onDeleteFile} />
        </CloseButtonWrapper>
      </Container>
    </CustomFileRowWrapper>
  );
};

export const CustomUploadFilesList = ({ isUploading, onUpload }) => {
  const files = useUploadFormFiles();
  const dispatch = useDispatch();

  const handleUploadClick = () => {
    onUpload(files);
  };

  const onEditFile = (file, key, value) => {
    dispatch({
      type: uploadFilesActions.updateFiles,
      payload: [{ prevName: file.name, changes: { [key]: value } }],
    });
  };

  return (
    <Wrapper>
      {files.map((file) => (
        <CustomFileRow key={file.name} file={file} onEditFile={onEditFile} />
      ))}
      {files.length > 0 && (
        <Container top={24} style={{ textAlign: 'center' }}>
          <FlatButton fullWidth onClick={handleUploadClick} disabled={isUploading || files.length === 0}>
            Upload File
          </FlatButton>
        </Container>
      )}
    </Wrapper>
  );
};
